import React, { useEffect, useMemo, useState } from "react";
import { Breadcrumbs } from "@truevault/aquilo";
import { NetworkRequest } from "../../../../common/models";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../root/routes";
import { AccessRequestInstructionsPage } from "../../requestInstructions/AccessRequestInstructionsPage";
import { CorrectionInstructionsPage } from "../../requestInstructions/CorrectionInstructionsPage";
import { DeleteInstructionsPage } from "../../requestInstructions/DeleteInstructionsPage";
import {
  buildStepsFromWorkflowType,
  HorizontalStepProgressIndicator,
} from "../../requestInstructions/HorizontalStepProgressIndicator";
import {
  filterOptOutVendors,
  OptOutInstructionsPage,
} from "../../requestInstructions/OptOutInstructionsPage";
import { ObjectionInstructionsPage } from "../../requestInstructions/ObjectionInstructionsPage";
import { PortabilityInstructionsPage } from "../../requestInstructions/PortabilityInstructionsPage";
import { RestrictProcessingInstructionsPage } from "../../requestInstructions/RestrictProcessingInstructionsPage";
import { WithdrawalOfConsentInstructionsPage } from "../../requestInstructions/WithdrawalOfConsentInstructionsPage";
import { WorkflowHelpCard } from "../../requestInstructions/WorkflowInformationCard";
import {
  RegionGroup,
  RegulationSlug,
  WorkflowType,
} from "../../../../common/service/server/dto/RegulationRequestTypeDto";
import {
  useOrganizationVendors,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
} from "../../../hooks";
import { useOrgRegulationRequestTypes } from "../../../hooks/useOrgRegulationRequestTypes";
import { DataStorageVendorSettingsTable } from "../workflows/DataStorageSettingsPage";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../get-compliant/data-recipients/shared";
import { useManagedAdNetworks } from "../../../../common/hooks/dataRecipientSettingsHooks";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../PrivacyNoticeChecklistItems";
import { DevInstructionsFinishedHrDsarWorkflowsSlug } from "./HRCenterDashboardPage";
import { useHistory } from "react-router";
import { useEmploymentCollectionGroups } from "../../../hooks/useCollectionGroups";
import {
  hasCaliforniaEmploymentGroup,
  hasEeaUkEmploymentGroup,
} from "../../../util/consumerGroups";

type HRDSARWorkflowView =
  | "data-storage"
  | "deletion"
  | "access"
  | "correction"
  | "withdrawal-of-consent"
  | "portability"
  | "objection"
  | "restrict-processing"
  | "opt-out";

const navItems: {
  key: HRDSARWorkflowView;
  label: string;
  workflowType?: WorkflowType;
  showDividerAfter?: boolean;
}[] = [
  { key: "data-storage", label: "Data Storage", showDividerAfter: true },
  { key: "deletion", label: "Deletion", workflowType: WorkflowType.DELETE },
  { key: "access", label: "Access", workflowType: WorkflowType.KNOW },
  { key: "correction", label: "Correction", workflowType: WorkflowType.CORRECTION },
  {
    key: "withdrawal-of-consent",
    label: "Withdrawal of Consent",
    workflowType: WorkflowType.WITHDRAW_CONSENT,
  },
  { key: "portability", label: "Portability", workflowType: WorkflowType.PORTABILITY },
  { key: "objection", label: "Objection", workflowType: WorkflowType.OBJECTION },
  {
    key: "restrict-processing",
    label: "Restrict Processing",
    workflowType: WorkflowType.RESTRICT_PROCESSING,
  },
  { key: "opt-out", label: "Opt-Out", workflowType: WorkflowType.OPT_OUT },
];

const stepProgressCurrentStepByView: Partial<Record<HRDSARWorkflowView, number>> = {
  deletion: 2,
  access: 1,
  correction: 1,
  "opt-out": 1,
};

export function HRDSARWorkflowsPage() {
  const history = useHistory();
  const organizationId = usePrimaryOrganizationId();
  const [organization] = usePrimaryOrganization();
  // `?view=access` opens a specific workflow, e.g. from the Access request step's "Go to Access setup" link.
  const [selectedView, setSelectedView] = useState<HRDSARWorkflowView>(() => {
    const requestedView = new URLSearchParams(history.location.search).get("view");
    return navItems.find((item) => item.key === requestedView)?.key ?? "data-storage";
  });
  const { regulationRequestTypes, loading: requestTypesLoading } = useOrgRegulationRequestTypes();
  const [employmentCollectionGroups, employmentCollectionGroupsRequest] =
    useEmploymentCollectionGroups(organizationId);
  const [allVendors] = useOrganizationVendors(
    organizationId,
    // All following args are default values from function signature, except for context and onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ ["EMPLOYEE"],
    /* onlyUseSettled = */ true,
  );
  const { answers: sharingAnswers, request: sharingSurveyRequest } = useSurvey(
    organizationId,
    SELLING_AND_SHARING_SURVEY_NAME,
  );
  const { answers: devInstructionsSurveyAnswers, setAnswer: setDevSurveyAnswer } = useSurvey(
    organizationId,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const isFinishedWithHrDsarWorkflows =
    devInstructionsSurveyAnswers?.[DevInstructionsFinishedHrDsarWorkflowsSlug] === "true";
  const { managedAdNetworks, isLoading: managedAdNetworksLoading } =
    useManagedAdNetworks(organizationId);

  const hrRequestTypes = useMemo(() => {
    if (requestTypesLoading || !NetworkRequest.isFinished(employmentCollectionGroupsRequest)) {
      return undefined;
    }

    const hasCaliforniaEmployees = hasCaliforniaEmploymentGroup(employmentCollectionGroups);
    const hasEeaUkEmployees = hasEeaUkEmploymentGroup(employmentCollectionGroups);

    return regulationRequestTypes.filter(
      // Keep this aligned with NewRequestForm: HR requests are available for
      // CCPA (except Limit) and EEA/UK request types.
      (requestType) =>
        (hasCaliforniaEmployees &&
          requestType.regulationLaw.slug === RegulationSlug.CCPA &&
          requestType.workflowType !== WorkflowType.LIMIT) ||
        (hasEeaUkEmployees && requestType.regulationLaw.region?.regionGroup === RegionGroup.EEA_UK),
    );
  }, [
    employmentCollectionGroups,
    employmentCollectionGroupsRequest,
    regulationRequestTypes,
    requestTypesLoading,
  ]);

  const hrWorkflowTypes = useMemo(
    () =>
      hrRequestTypes
        ? new Set(hrRequestTypes.map((requestType) => requestType.workflowType))
        : undefined,
    [hrRequestTypes],
  );

  const hasOptOutVendors = useMemo(() => {
    if (
      allVendors === undefined ||
      !NetworkRequest.areFinished(sharingSurveyRequest) ||
      managedAdNetworksLoading
    ) {
      return undefined;
    }

    return filterOptOutVendors(allVendors, sharingAnswers, managedAdNetworks).vendors.length > 0;
  }, [
    allVendors,
    managedAdNetworks,
    managedAdNetworksLoading,
    sharingAnswers,
    sharingSurveyRequest,
  ]);

  const isViewAvailable = (view: HRDSARWorkflowView): boolean | undefined => {
    const workflowType = navItems.find((item) => item.key === view)?.workflowType;
    if (!workflowType) {
      return true;
    }
    if (!hrWorkflowTypes) {
      return undefined;
    }
    if (!hrWorkflowTypes.has(workflowType)) {
      return false;
    }
    if (view === "opt-out") {
      return hasOptOutVendors;
    }
    return true;
  };

  const selectedViewIsHidden = isViewAvailable(selectedView) === false;

  useEffect(() => {
    if (selectedViewIsHidden) {
      setSelectedView("data-storage");
    }
  }, [selectedViewIsHidden]);

  const visibleNavItems = navItems.filter((item) => isViewAvailable(item.key) !== false);
  const activeView = selectedViewIsHidden ? "data-storage" : selectedView;
  const activeNavItemIndex = visibleNavItems.findIndex((item) => item.key === activeView);
  const nextNavItem = activeNavItemIndex >= 0 ? visibleNavItems[activeNavItemIndex + 1] : undefined;
  const hasOptOutRequestType = hrWorkflowTypes?.has(WorkflowType.OPT_OUT) ?? false;
  const isNavigationReady =
    !requestTypesLoading &&
    NetworkRequest.isFinished(employmentCollectionGroupsRequest) &&
    (!hasOptOutRequestType || hasOptOutVendors !== undefined);
  const activeWorkflowType = navItems.find((item) => item.key === activeView)?.workflowType;
  const activeWorkflowRequestTypes = activeWorkflowType
    ? hrRequestTypes?.filter((requestType) => requestType.workflowType === activeWorkflowType) ?? []
    : [];
  const stepProgressCurrentStep = stepProgressCurrentStepByView[activeView];
  const stepProgressSteps =
    activeWorkflowType && stepProgressCurrentStep !== undefined
      ? buildStepsFromWorkflowType(activeWorkflowType, stepProgressCurrentStep)
      : null;

  const handleStepProgressClick = (stepId: string) => {
    document.getElementById(stepId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        !isFinishedWithHrDsarWorkflows ? (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent={nextNavItem ? "Next" : "Done"}
                nextOnClick={async () => {
                  if (!isNavigationReady) {
                    return;
                  }

                  if (nextNavItem) {
                    setSelectedView(nextNavItem.key);
                    return;
                  }

                  await setDevSurveyAnswer(DevInstructionsFinishedHrDsarWorkflowsSlug, "true");
                  history.push(Routes.hrCenter.root);
                }}
                nextDisabled={!isNavigationReady}
              />
            }
          />
        ) : undefined
      }
    >
      <div className="tw-boundary mb-8">
        <Breadcrumbs
          source="HR Center"
          title="HR DSAR Workflows"
          sourceRoute={Routes.hrCenter.root}
        />
      </div>

      <div className="tw-boundary">
        <div className="flex items-start gap-8">
          <nav className="w-56 flex-shrink-0" aria-label="HR DSAR workflows">
            <ul className="space-y-1">
              {visibleNavItems.map((item) => {
                const selected = activeView === item.key;
                return (
                  <li
                    key={item.key}
                    className={
                      item.showDividerAfter ? "mb-2 border-b border-gray-200 pb-2" : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedView(item.key)}
                      className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                        selected
                          ? "bg-primary-50 text-primary-700"
                          : "text-neutral-700 hover:bg-neutral-100"
                      }`}
                    >
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div
            className={`min-w-0 flex-1 ${
              activeView === "data-storage" ? "max-w-[1160px] overflow-x-auto" : "max-w-[776px]"
            }`}
          >
            {activeWorkflowType && (
              <WorkflowHelpCard
                workflowType={activeWorkflowType}
                requestTypes={activeWorkflowRequestTypes}
                organization={organization ?? null}
                dataStorageSettingsUrl={Routes.hrCenter.hrDsarWorkflows}
              />
            )}

            {stepProgressSteps && (
              <HorizontalStepProgressIndicator
                steps={stepProgressSteps}
                onStepClick={handleStepProgressClick}
              />
            )}

            {activeView === "data-storage" ? (
              <DataStorageVendorSettingsTable
                orgId={organizationId}
                vendorsCollectionContexts={["EMPLOYEE"]}
              />
            ) : activeView === "deletion" ? (
              <DeleteInstructionsPage
                collectionContext={["EMPLOYEE"]}
                dataMapCollectionGroupType="EMPLOYMENT"
                embedded
              />
            ) : activeView === "opt-out" ? (
              <OptOutInstructionsPage collectionContext={["EMPLOYEE"]} embedded />
            ) : activeView === "access" ? (
              <AccessRequestInstructionsPage collectionContext={["EMPLOYEE"]} embedded />
            ) : activeView === "correction" ? (
              <CorrectionInstructionsPage embedded />
            ) : activeView === "withdrawal-of-consent" ? (
              <WithdrawalOfConsentInstructionsPage embedded />
            ) : activeView === "portability" ? (
              <PortabilityInstructionsPage embedded />
            ) : activeView === "objection" ? (
              <ObjectionInstructionsPage embedded />
            ) : (
              <RestrictProcessingInstructionsPage embedded />
            )}
          </div>
        </div>
      </div>
    </StandardPageWrapper>
  );
}
